import React, { useState } from 'react';
import { CheckCircle, AlertCircle, Star, BookOpen, Lightbulb, Target, Users, Palette, MessageCircle, Layers, Award, TrendingUp } from 'lucide-react';

// 糖果色配色方案（从 logo 提取）
const candyColors = {
  pink: '#E91E63',
  green: '#4CAF50',
  yellow: '#FFEB3B',
  purple: '#9C27B0',
  lightBlue: '#2196F3',
  darkBlue: '#1976D2',
  orange: '#FF9800',
  amber: '#FFC107',
  cyan: '#00BCD4',
  teal: '#009688',
};

// 配色方案数组，用于循环使用
const colorPalette = [
  candyColors.pink,
  candyColors.green,
  candyColors.yellow,
  candyColors.purple,
  candyColors.lightBlue,
  candyColors.darkBlue,
  candyColors.orange,
  candyColors.amber,
  candyColors.cyan,
  candyColors.teal,
];

const CourseStandards: React.FC = () => {
  const [expandedSection, setExpandedSection] = useState<string>('section1');

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? '' : section);
  };

  const SectionCard: React.FC<{
    title: string;
    icon: React.ReactNode;
    colorIndex: number;
    children: React.ReactNode;
    sectionKey: string;
  }> = ({ title, icon, colorIndex, children, sectionKey }) => {
    const isExpanded = expandedSection === sectionKey;
    const color = colorPalette[colorIndex % colorPalette.length];
    
    return (
      <div className="mb-6 rounded-2xl overflow-hidden shadow-lg border-2 transition-all duration-300 hover:shadow-xl" style={{ borderColor: color + '20' }}>
        <button
          onClick={() => toggleSection(sectionKey)}
          className="w-full px-6 py-4 flex items-center justify-between transition-colors"
          style={{ backgroundColor: color + '15' }}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl" style={{ backgroundColor: color }}>
              {icon}
            </div>
            <h2 className="text-xl font-bold" style={{ color }}>{title}</h2>
          </div>
          <div
            className={`transform transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
            style={{ color }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
        </button>
        
        {isExpanded && (
          <div className="px-6 py-5 bg-white">
            {children}
          </div>
        )}
      </div>
    );
  };

  const HardRequirementItem: React.FC<{ number: number; text: string }> = ({ number, text }) => (
    <div className="flex gap-4 mb-4 p-4 rounded-xl bg-gradient-to-r from-pink-50 to-white border-l-4 border-pink-500">
      <div
        className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md"
        style={{ background: `linear-gradient(135deg, ${candyColors.pink}, ${candyColors.purple})` }}
      >
        {number}
      </div>
      <p className="text-gray-700 leading-relaxed pt-2">{text}</p>
    </div>
  );

  const InfoItem: React.FC<{ number: number; text: string; colorIndex: number }> = ({ number, text, colorIndex }) => {
    const color = colorPalette[colorIndex % colorPalette.length];
    return (
      <div className="flex gap-3 mb-3 p-3 rounded-lg" style={{ backgroundColor: color + '10' }}>
        <div
          className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm shadow"
          style={{ backgroundColor: color }}
        >
          {number}
        </div>
        <p className="text-gray-700 leading-relaxed pt-1.5">{text}</p>
      </div>
    );
  };

  const PageTypeCard: React.FC<{ title: string; requirements: string[]; colorIndex: number }> = ({ title, requirements, colorIndex }) => {
    const color = colorPalette[colorIndex % colorPalette.length];
    return (
      <div className="mb-4 p-4 rounded-xl border-2 transition-all hover:shadow-md" style={{ borderColor: color + '30', backgroundColor: color + '08' }}>
        <h4 className="font-bold text-lg mb-3 flex items-center gap-2" style={{ color }}>
          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: color }}></span>
          {title}
        </h4>
        <ul className="space-y-2">
          {requirements.map((req, idx) => (
            <li key={idx} className="flex items-start gap-2 text-gray-700">
              <CheckCircle size={18} className="mt-0.5 flex-shrink-0" style={{ color }} />
              <span>{req}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const ScoringTable = () => {
    const dimensions = [
      { name: '目标清晰', score10: '老师一眼看懂这页教什么、练什么、做到什么', score6: '大致知道，但不够明确', score0: '看完仍不知道这页到底干嘛' },
      { name: '环节边界', score10: '明确知道何时讲、何时玩、何时开工具、何时收', score6: '有提示，但节拍略糊', score0: '老师无法判断课堂节奏' },
      { name: '3W1H 沉淀', score10: '隐喻后有正式讲解，知识名词和步骤都清楚', score6: '有讲解，但不完整', score0: '基本只有故事和隐喻' },
      { name: '操作脚手架', score10: '第一步、第一句 Prompt、预期结果都明确', score6: '有操作引导，但还要老师自己补', score0: '学生根本不知道怎么开始' },
      { name: '形成性检测', score10: '有清晰过关标准，老师能当场判断会没会', score6: '有检测但偏虚', score0: '没有测试反馈' },
      { name: '跨页连贯', score10: '前后页接得顺，为什么翻页很清楚', score6: '大体连贯，但有小跳点', score0: '突兀、断档、像拼装' },
      { name: '页面负载', score10: '一页一件主事，信息密度舒服', score6: '稍有堆叠，但还能带', score0: '一页塞太多任务' },
      { name: '语言质量', score10: '口语自然、去 AI 味、老师敢直接说', score6: '偶尔有表演腔', score0: '明显 AI 味、主持腔、说教腔' },
      { name: '视觉护眼', score10: '字大清楚、动效克制、主题路由合理', score6: '基本可看，但有些刺激或拥挤', score0: '小字、眩晕、红警报泛滥' },
      { name: '教师可执行性', score10: '新老师拿着就能带', score6: '还需自己临场补不少', score0: '没法直接上课' },
    ];

    return (
      <div className="overflow-x-auto rounded-xl border-2 border-purple-200">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ backgroundColor: candyColors.purple + '20' }}>
              <th className="px-4 py-3 text-left font-bold text-purple-900 border-b-2 border-purple-300">维度</th>
              <th className="px-4 py-3 text-left font-bold text-green-800 border-b-2 border-green-300 bg-green-100">10 分</th>
              <th className="px-4 py-3 text-left font-bold text-orange-800 border-b-2 border-orange-300 bg-orange-100">6 分</th>
              <th className="px-4 py-3 text-left font-bold text-red-800 border-b-2 border-red-300 bg-red-100">0 分</th>
            </tr>
          </thead>
          <tbody>
            {dimensions.map((dim, idx) => (
              <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                <td className="px-4 py-3 font-semibold text-gray-800 border-b border-gray-200">{dim.name}</td>
                <td className="px-4 py-3 text-gray-700 border-b border-gray-200 bg-green-50">{dim.score10}</td>
                <td className="px-4 py-3 text-gray-700 border-b border-gray-200 bg-orange-50">{dim.score6}</td>
                <td className="px-4 py-3 text-gray-700 border-b border-gray-200 bg-red-50">{dim.score0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const ChecklistCard = () => (
    <div className="p-6 rounded-2xl border-2 border-dashed" style={{ borderColor: candyColors.teal, backgroundColor: candyColors.teal + '08' }}>
      <h3 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: candyColors.teal }}>
        <Target size={24} />
        逐页验收检查表
      </h3>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div className="flex items-center gap-2 p-2 rounded-lg bg-white border" style={{ borderColor: candyColors.pink }}>
            <input type="text" placeholder="页码" className="flex-1 outline-none text-sm p-1" />
          </div>
          <div className="flex items-center gap-2 p-2 rounded-lg bg-white border" style={{ borderColor: candyColors.green }}>
            <input type="text" placeholder="页面模式" className="flex-1 outline-none text-sm p-1" />
          </div>
          <div className="flex items-center gap-2 p-2 rounded-lg bg-white border" style={{ borderColor: candyColors.darkBlue }}>
            <input type="text" placeholder="教师信号" className="flex-1 outline-none text-sm p-1" />
          </div>
          <div className="flex items-center gap-2 p-2 rounded-lg bg-white border" style={{ borderColor: candyColors.purple }}>
            <input type="text" placeholder="工具状态" className="flex-1 outline-none text-sm p-1" />
          </div>
          <div className="flex items-center gap-2 p-2 rounded-lg bg-white border" style={{ borderColor: candyColors.orange }}>
            <input type="text" placeholder="本页主任务" className="flex-1 outline-none text-sm p-1" />
          </div>
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-2 p-2 rounded-lg bg-white border" style={{ borderColor: candyColors.cyan }}>
            <input type="text" placeholder="第一动作" className="flex-1 outline-none text-sm p-1" />
          </div>
          <div className="flex items-center gap-2 p-2 rounded-lg bg-white border" style={{ borderColor: candyColors.teal }}>
            <input type="text" placeholder="第一条提示词" className="flex-1 outline-none text-sm p-1" />
          </div>
          <div className="flex items-center gap-2 p-2 rounded-lg bg-white border" style={{ borderColor: candyColors.amber }}>
            <input type="text" placeholder="过关标准" className="flex-1 outline-none text-sm p-1" />
          </div>
          <div className="flex items-center gap-2 p-2 rounded-lg bg-white border" style={{ borderColor: candyColors.lightBlue }}>
            <input type="text" placeholder="下一页为什么翻" className="flex-1 outline-none text-sm p-1" />
          </div>
          <div className="flex items-center gap-2 p-2 rounded-lg bg-white border" style={{ borderColor: candyColors.pink }}>
            <select className="flex-1 outline-none text-sm p-1 bg-transparent">
              <option>是否达标：是 / 否</option>
              <option>是</option>
              <option>否</option>
            </select>
          </div>
        </div>
      </div>
      <div className="mt-4 p-3 rounded-lg bg-white border-2" style={{ borderColor: candyColors.yellow }}>
        <p className="text-sm font-semibold text-gray-700">本页评分（10 项）：____________________</p>
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="inline-block px-8 py-4 rounded-3xl bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 text-white shadow-lg mb-4">
          <h1 className="text-3xl font-black flex items-center gap-3">
            <Award size={36} />
            课件研发标准
          </h1>
        </div>
        <p className="text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed">
          这套标准给老师直接用来逐页验收 <span className="font-bold text-pink-600">设计稿 + 页边教案</span>。
          先过<span className="font-bold text-orange-600">"硬门槛"</span>，再打 <span className="font-bold text-green-600">100 分</span>。
          没过硬门槛，直接退回，不进入高分评审。
        </p>
      </div>

      {/* Section 1: 硬门槛 */}
      <SectionCard
        title="一、先过 8 条硬门槛"
        icon={<AlertCircle size={28} color="white" />}
        colorIndex={0}
        sectionKey="section1"
      >
        <div className="space-y-2">
          <HardRequirementItem number={1} text="任意可见文字 ≥ 28px，放不下就拆页，不能缩字。" />
          <HardRequirementItem number={2} text="阶段提示只能做成左上或右上角标，不能压在页头下方占正文空间。" />
          <HardRequirementItem number={3} text="页面必须显性写清 本页课堂模式 / 教师信号 / 工具状态。" />
          <HardRequirementItem number={4} text="一页只允许 一个主任务，最多辅带一个次任务，不能又讲又玩又测又转场全堆一起。" />
          <HardRequirementItem number={5} text="任何隐喻、故事、不插电体验之后，必须有至少一页正式 3W1H 讲解沉淀。" />
          <HardRequirementItem number={6} text="任何实操页必须写清 何时打开工具 / 第一步做什么 / 第一句提示词是什么 / 预期看到什么。" />
          <HardRequirementItem number={7} text="每学完一个知识点、一个环节、一个实操块，必须有形成性检测，不允许'学完了但没人知道会没会'。" />
          <HardRequirementItem number={8} text="禁止课堂不适宜词和过猛表达，如 毒气室 / 俄罗斯轮盘 / 拉肚子 / 跑厕所 / 背锅 / 医药费 / 毒王 / 死得快 / 狠狠砸 / 蹂躏 / 塌房。" />
        </div>
      </SectionCard>

      {/* Section 2: 9 个信息 */}
      <SectionCard
        title="二、每页必须交代的 9 个信息"
        icon={<BookOpen size={28} color="white" />}
        colorIndex={1}
        sectionKey="section2"
      >
        <div className="grid md:grid-cols-2 gap-3">
          <InfoItem number={1} text="这页属于哪个环节。" colorIndex={0} />
          <InfoItem number={2} text="这页是 讲解页 / 活动页 / Vibe 实操页 / 微测页 / 收口页 / 转场页 / 总结页 中的哪一种。" colorIndex={1} />
          <InfoItem number={3} text="老师这页先讲还是先让学生做。" colorIndex={2} />
          <InfoItem number={4} text="学生具体要做哪一个动作。" colorIndex={3} />
          <InfoItem number={5} text="如果要上机，什么时候打开 Vibe Coding。" colorIndex={4} />
          <InfoItem number={6} text="如果要输入提示词，第一句示例是什么。" colorIndex={5} />
          <InfoItem number={7} text="这页学的核心知识或技能是什么。" colorIndex={6} />
          <InfoItem number={8} text="这页完成后，老师怎么判断'过关'。" colorIndex={7} />
          <InfoItem number={9} text="下一页为什么要去、怎么去。" colorIndex={8} />
        </div>
      </SectionCard>

      {/* Section 3: 页型专项标准 */}
      <SectionCard
        title="三、按页型执行的专项标准"
        icon={<Layers size={28} color="white" />}
        colorIndex={2}
        sectionKey="section3"
      >
        <div className="grid md:grid-cols-2 gap-4">
          <PageTypeCard
            title="P01 封面页"
            colorIndex={0}
            requirements={[
              '只负责 看封面 -> 听一句开场 -> 点进入下一页',
              '不允许把视差、粒子、按钮特效写成课堂活动',
              '老师话术控制在 20-40 秒，不能长篇世界观表演'
            ]}
          />
          <PageTypeCard
            title="P02 特殊视频页"
            colorIndex={1}
            requirements={[
              '默认 老师统一播放，全班统一观看',
              '视频必须和 课主题 / 至少 1 个知识点冲突 / 最终项目 / 学完能做什么 对齐',
              '视频结束后，老师必须用一句话钉住任务书'
            ]}
          />
          <PageTypeCard
            title="正式讲解页"
            colorIndex={2}
            requirements={[
              '必须写出 Why / What / How / How Well',
              '不允许只剩隐喻，没有正式知识名词和明确步骤'
            ]}
          />
          <PageTypeCard
            title="不插电活动页"
            colorIndex={3}
            requirements={[
              '必须写清 物料 / 规则 / 教师口令 / 停止点 / 收口点',
              '不能只写"玩什么"，不写"老师何时叫停、怎么复盘"'
            ]}
          />
          <PageTypeCard
            title="Vibe 实操页"
            colorIndex={4}
            requirements={[
              '必须写清 打开工具信号 / 第一条 Prompt / 预期反馈 / 暂停纠偏 / 再练一次',
              '必须让老师看得出"现在是讲，还是练"'
            ]}
          />
          <PageTypeCard
            title="微测页"
            colorIndex={5}
            requirements={[
              '必须做到 一个问题 + 一个动作 + 一个答案揭示',
              '不允许微测页变成大段仪式或表演页'
            ]}
          />
          <PageTypeCard
            title="收口转场页"
            colorIndex={6}
            requirements={[
              '必须回答 刚学会了什么 / 为什么下一步值得学',
              '不允许只靠黑屏、警报、悬念吓人'
            ]}
          />
          <PageTypeCard
            title="总结/交付页"
            colorIndex={7}
            requirements={[
              '只能回看成果、学到的知识、下一步应用',
              '不允许再抛新知识'
            ]}
          />
        </div>
      </SectionCard>

      {/* Section 4: 视觉与动效 */}
      <SectionCard
        title="四、视觉与动效标准"
        icon={<Palette size={28} color="white" />}
        colorIndex={3}
        sectionKey="section4"
      >
        <div className="space-y-3">
          <div className="p-4 rounded-xl bg-gradient-to-r from-blue-50 to-purple-50 border-l-4 border-blue-500">
            <p className="text-gray-700"><span className="font-bold text-blue-600">深色页</span>只用于 危机引入 / 视频沉浸 / 少量高潮页。</p>
          </div>
          <div className="p-4 rounded-xl bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-500">
            <p className="text-gray-700"><span className="font-bold text-orange-600">浅色页</span>优先用于 正式讲解 / 微测 / 复盘 / 总结 / 不插电规则页。</p>
          </div>
          <div className="p-4 rounded-xl bg-gradient-to-r from-green-50 to-teal-50 border-l-4 border-green-500">
            <p className="text-gray-700"><span className="font-bold text-green-600">Vibe 页</span>优先中性低饱和。</p>
          </div>
          <div className="p-4 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 border-l-4 border-red-500">
            <p className="text-gray-700"><span className="font-bold text-red-600">禁止</span>高频全屏闪烁、纯红大面积警报、持续剧烈抖动、长时间绿色数据雨。</p>
          </div>
          <div className="p-4 rounded-xl bg-gradient-to-r from-purple-50 to-pink-50 border-l-4 border-purple-500">
            <p className="text-gray-700"><span className="font-bold text-purple-600">高光反馈</span>只能用 局部亮起 / 局部粒子 / 小范围脉冲 / 轻微短震。</p>
          </div>
          <div className="p-4 rounded-xl bg-gradient-to-r from-cyan-50 to-blue-50 border-l-4 border-cyan-500">
            <p className="text-gray-700"><span className="font-bold text-cyan-600">字号要求</span>页头、角标、按钮、说明字全部执行 ≥28px。</p>
          </div>
        </div>
      </SectionCard>

      {/* Section 5: 语言标准 */}
      <SectionCard
        title="五、语言标准"
        icon={<MessageCircle size={28} color="white" />}
        colorIndex={4}
        sectionKey="section5"
      >
        <div className="grid md:grid-cols-2 gap-4">
          <div className="p-5 rounded-xl bg-green-50 border-2 border-green-200">
            <h4 className="font-bold text-green-800 mb-3 flex items-center gap-2">
              <CheckCircle size={20} />
              推荐使用
            </h4>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-1">✓</span>
                <span>老师话术要像<span className="font-semibold">真老师会说的话</span>，不要像广告词、游戏主持词、AI 宣言</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-1">✓</span>
                <div>
                  <p className="font-semibold mb-1">优先用词：</p>
                  <p>主理人 / 测试员 / 同桌互测 / 发现漏洞 / 记录问题 / 再确认 / 继续下一步</p>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-1">✓</span>
                <span>责任感优先写成 <span className="font-semibold">机器算，人复核；发布前再确认</span>，不要靠惩罚、羞辱、身体后果制造压力</span>
              </li>
            </ul>
          </div>
          <div className="p-5 rounded-xl bg-red-50 border-2 border-red-200">
            <h4 className="font-bold text-red-800 mb-3 flex items-center gap-2">
              <AlertCircle size={20} />
              避免使用
            </h4>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-red-500 mt-1">✗</span>
                <span>广告词、游戏主持词、AI 宣言风格</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 mt-1">✗</span>
                <div>
                  <p className="font-semibold mb-1">少用词汇：</p>
                  <p>红蓝军 / 战斗 / 发车 / 破坏者 / 摧残 / 塌房 / 英雄登场</p>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 mt-1">✗</span>
                <span>靠惩罚、羞辱、身体后果制造压力</span>
              </li>
            </ul>
          </div>
        </div>
      </SectionCard>

      {/* Section 6: 环节级连贯性 */}
      <SectionCard
        title="六、环节级连贯性标准"
        icon={<TrendingUp size={28} color="white" />}
        colorIndex={5}
        sectionKey="section6"
      >
        <div className="p-6 rounded-2xl bg-gradient-to-r from-purple-50 via-pink-50 to-blue-50 border-2 border-purple-200">
          <p className="text-lg text-gray-700 mb-4 font-semibold">每个环节都要满足这条链：</p>
          <div className="flex flex-wrap items-center gap-3 justify-center">
            {['钩子/任务', '体验/隐喻', '正式 3W1H 讲解', '练习/实操', '微测/过关', '收口/转场'].map((item, idx, arr) => (
              <React.Fragment key={idx}>
                <div
                  className="px-5 py-3 rounded-xl text-white font-bold shadow-lg transform hover:scale-105 transition-transform"
                  style={{ background: `linear-gradient(135deg, ${colorPalette[idx % colorPalette.length]}, ${colorPalette[(idx + 1) % colorPalette.length]})` }}
                >
                  {item}
                </div>
                {idx < arr.length - 1 && (
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" className="text-gray-400">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </React.Fragment>
            ))}
          </div>
          <p className="mt-5 text-gray-700 text-center italic">
            如果任一环节缺了中间的<span className="font-bold text-pink-600">"正式讲解"</span>或<span className="font-bold text-blue-600">"微测过关"</span>，老师就会重新迷茫。
          </p>
        </div>
      </SectionCard>

      {/* Section 7: 评分细则 */}
      <SectionCard
        title="七、100 分评分细则"
        icon={<Star size={28} color="white" />}
        colorIndex={6}
        sectionKey="section7"
      >
        <div className="mb-6 p-4 rounded-xl bg-yellow-50 border-2 border-yellow-300">
          <p className="text-gray-700 font-semibold">
            每项只打 <span className="text-yellow-600 font-bold">3 档</span>：10 分 / 6 分 / 0 分
          </p>
        </div>
        <ScoringTable />
      </SectionCard>

      {/* Section 8: 总分解释 */}
      <SectionCard
        title="八、总分解释"
        icon={<Award size={28} color="white" />}
        colorIndex={7}
        sectionKey="section8"
      >
        <div className="grid md:grid-cols-4 gap-4">
          <div className="p-5 rounded-2xl bg-gradient-to-br from-green-400 to-green-600 text-white text-center shadow-lg transform hover:scale-105 transition-transform">
            <div className="text-3xl font-black mb-2">95-100</div>
            <div className="text-sm opacity-90">可直接进课堂</div>
          </div>
          <div className="p-5 rounded-2xl bg-gradient-to-br from-blue-400 to-blue-600 text-white text-center shadow-lg transform hover:scale-105 transition-transform">
            <div className="text-3xl font-black mb-2">85-94</div>
            <div className="text-sm opacity-90">能上，但必须小修</div>
          </div>
          <div className="p-5 rounded-2xl bg-gradient-to-br from-orange-400 to-orange-600 text-white text-center shadow-lg transform hover:scale-105 transition-transform">
            <div className="text-3xl font-black mb-2">70-84</div>
            <div className="text-sm opacity-90">老师仍会焦虑和迷茫，不建议直接用</div>
          </div>
          <div className="p-5 rounded-2xl bg-gradient-to-br from-red-400 to-red-600 text-white text-center shadow-lg transform hover:scale-105 transition-transform">
            <div className="text-3xl font-black mb-2">&lt;70</div>
            <div className="text-sm opacity-90">退回重做</div>
          </div>
        </div>
      </SectionCard>

      {/* Section 9: 验收检查表 */}
      <SectionCard
        title="九、建议老师逐页验收时直接填这张表"
        icon={<CheckCircle size={28} color="white" />}
        colorIndex={8}
        sectionKey="section9"
      >
        <ChecklistCard />
      </SectionCard>
    </div>
  );
};

export default CourseStandards;
